export type PaperSize = 'A4' | 'A5' | 'A6' | 'thermal80mm' | 'thermal58mm' | 'custom';
export type Orientation = 'portrait' | 'landscape';
export type ElementType = 'text' | 'field' | 'image' | 'shape' | 'table' | 'barcode';
export type Language = 'ar' | 'en';
export type TextAlign = 'right' | 'left' | 'center' | 'justify';
export type BarcodeType = 'code128' | 'qrcode' | 'ean13';

export interface IElementStyle {
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  textAlign: TextAlign;
  color: string;
  backgroundColor: string;
  width: number;
  height: number;
  borderStyle: string;
  borderWidth: number;
  borderColor: string;
  padding: number;
}

export interface IPosition {
  x: number;
  y: number;
  zIndex: number;
  rotation?: number;
}

export interface ITableColumn {
  field: string;
  label: string;
  width: number;
  visible?: boolean;
}

export interface ITableStyleConfig {
  headerBackground: string;
  headerTextColor: string;
  rowBackground: string;
  rowTextColor: string;
  alternateRowBackground: string;
}

export interface ITableConfig {
  showHeaders: boolean;
  columns: ITableColumn[];
  styles: ITableStyleConfig;
  populateFromInvoice?: boolean;
  compactView?: boolean;
  fontSize?: number;
  headerFontSize?: number; // خاصية جديدة لمقاس خط رأس الجدول (الأعمدة)
  dynamicHeight?: boolean; // خاصية لجعل الجدول ديناميكي يتمدد حسب عدد المنتجات
  pushElementsDown?: boolean; // خاصية لإزاحة العناصر تحت الجدول تلقائياً
}

export interface IBarcodeConfig {
  barcodeType: BarcodeType;
  showText: boolean;
  isZATCAQR?: boolean; // خاصية لتحديد QR الفاتورة الإلكترونية السعودية
  zatcaFields?: {
    sellerName: string; // اسم الشركة
    vatNumber: string; // الرقم الضريبي
    invoiceDate: string; // تاريخ الفاتورة
    invoiceTotal: string; // إجمالي الفاتورة
    vatAmount: string; // إجمالي الضريبة
  }; // حقول بيانات QR الفاتورة الإلكترونية
}

export interface ITemplateElement {
  id: string;
  elementType: ElementType;
  sourceField?: string;
  content?: string;
  style: IElementStyle;
  position: IPosition;
  tableConfig?: ITableConfig;
  barcodeConfig?: IBarcodeConfig;
  showFieldName?: boolean;
}

export interface ITemplateSnapshot {
  name: string;
  createdAt: string;
  elements: ITemplateElement[];
  paperSize: PaperSize;
  orientation: Orientation;
  customWidth?: number;
  customHeight?: number;
}

export interface IPrintTemplate {
  _id?: string;
  name: string;
  description?: string;
  invoiceType: string;
  paperSize: PaperSize;
  orientation: Orientation;
  customWidth?: number;
  customHeight?: number;
  displayLanguage: Language; // Renamed to match server model
  elements: ITemplateElement[];
  selectedFields?: IAvailableFieldOption[]; // الحقول المختارة في محرر التصميم
  isDefault: boolean;
  snapshots?: ITemplateSnapshot[];
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IPrintTemplateCreate {
  name: string;
  description?: string;
  invoiceType: string;
  paperSize?: PaperSize;
  orientation?: Orientation;
  customWidth?: number;
  customHeight?: number;
  displayLanguage?: Language; // Renamed to match server model
  elements?: ITemplateElement[];
  selectedFields?: IAvailableFieldOption[]; // الحقول المختارة في محرر التصميم
  isDefault?: boolean;
}

export interface IPrintTemplateUpdate extends Partial<IPrintTemplateCreate> {}

export interface IPrintTemplateResponse {
  success: boolean;
  data: {
    template: IPrintTemplate;
  };
  message?: string;
}

export interface IPrintTemplatesResponse {
  success: boolean;
  data: {
    templates: IPrintTemplate[];
    count: number;
  };
}

// خيارات المصادر المتاحة للعناصر المختلفة
export interface IAvailableFieldOption {
  field: string;
  label: string;
  description?: string;
  group: string;
}

// المجموعات المختلفة للحقول
export const fieldGroups = {
  BRANCH_INFO: 'معلومات الفرع',
  INVOICE_INFO: 'معلومات الفاتورة',
  CUSTOMER_INFO: 'معلومات العميل',
  SUPPLIER_INFO: 'معلومات المورد',
  PRODUCT_TABLE: 'جدول المنتجات',
  TOTALS: 'المجاميع',
  OTHER: 'أخرى'
} as const;